import {
    Component,
    computed,
    effect,
    ElementRef,
    HostListener,
    OnInit,
    signal,
    ViewChild,
    WritableSignal,
} from '@angular/core';
import {User} from '../../../models/user';
import {UserAuthService} from '../../../@core/user-auth.service';
import {
    FacilityChooserService,
    facilityOptionSearchFn,
    OrganizationFacilityDetails,
} from '../../../@core/facility-chooser.service';
import {UnsubscribeComponent} from '../../../@core/fc-component';
import {takeUntil} from 'rxjs/operators';
import {OrganizationSerializer} from '../../../@core/api.service';
import {
    longTransition,
    selectCaptionPulseAnimationTrigger,
    selectPulseAnimationTrigger,
    stateTransition,
} from '../../../@theme/animations';
import {animate, animateChild, group, query, style, transition, trigger} from '@angular/animations';
import {OverlayService} from '../../../@core/overlay.service';
import {CdkVirtualScrollViewport, VIRTUAL_SCROLL_STRATEGY} from '@angular/cdk/scrolling';
import {MethodCache} from '../../../utils/accessor.utils';
import {Facility} from '../../../models/facility';
import {EstimatedHeightVirtualScrollStrategy} from './estimated-height-virtual-scroll-strategy';

interface OrganizationFacilityDetailsViewModel extends OrganizationFacilityDetails {
    keyboardSelected: boolean;
}

type FlatListItemType = 'header' | 'inactive-header' | 'facility';

interface FlatListItem {
    type: FlatListItemType;
    headerText?: string;
    option?: OrganizationFacilityDetailsViewModel;
}

@Component({
    selector: 'app-facility-select',
    templateUrl: './facility-select.component.html',
    styleUrls: ['./facility-select.component.scss'],
    providers: [
        EstimatedHeightVirtualScrollStrategy,
        {
            provide: VIRTUAL_SCROLL_STRATEGY,
            useExisting: EstimatedHeightVirtualScrollStrategy,
        },
    ],
    animations: [
        trigger('popFromLeft', [
            transition('void => *', [
                style({transform: 'translateX(-16px)', opacity: '0'}),
                group([
                    query('@*', animateChild(), {optional: true}),
                    animate(longTransition),
                ]),
            ]),
            transition('* => void', [
                group([
                    query('@*', animateChild(), {optional: true}),
                    animate(stateTransition, style({transform: 'translateX(-16px)', opacity: '0'})),
                ]),
            ]),
        ]),
        trigger('popFromLeftInner', [
            transition('void => *', [
                style({transform: 'translateX(24px)'}),
                animate(longTransition),
            ]),
            transition('* => void', [
                animate(longTransition, style({transform: 'translateX(24px)'})),
            ]),
        ]),
        selectPulseAnimationTrigger,
        selectCaptionPulseAnimationTrigger,
    ],
})
export class FacilitySelectComponent extends UnsubscribeComponent implements OnInit {
    @ViewChild(CdkVirtualScrollViewport) virtualScroll: CdkVirtualScrollViewport;
    @ViewChild('facilitySearchInput') facilitySearchInputRef: ElementRef<HTMLInputElement>;

    private _user: User;
    organizations: OrganizationSerializer[];
    selected: OrganizationFacilityDetails;
    originalOptions: OrganizationFacilityDetailsViewModel[];
    adminView: boolean;


    readonly options: WritableSignal<OrganizationFacilityDetailsViewModel[]> = signal([]);
    readonly open = signal(false);
    readonly facilitySearchString = signal<string | null>(null);
    readonly keyboardSelectedIndex = signal<number>(-1);

    readonly flatList = computed(() => this.buildFlatListFromOptions(this.options()));

    readonly itemSize = 42; // Estimated height for initial sizing
    readonly minViewportHeight = 65; // Minimum height in pixels

    // Height constants for different item types
    readonly facilityHeightSingleLine = 35;
    readonly facilityHeightMultiLine = 42;
    readonly facilityHeightSingleLineWithActions = 49;
    readonly facilityHeightMultiLineWithActions = 56;
    readonly headerHeightSingleLine = 44.8;
    readonly headerHeightMultiLine = 62;
    readonly facilityCharThreshold = 28; // with admin action icons
    readonly facilityCharThresholdNoActions = 34; // without admin action icons (wider text area)
    readonly headerCharThreshold = 36;
    readonly contentWrapperPadding = 28;

    readonly viewportHeight = computed(() => {
        const items = this.flatList();
        const contentHeight = items.reduce((sum, item) => sum + this.estimateItemHeight(item), 0);
        const totalHeight = contentHeight + this.contentWrapperPadding;
        const maxAvailableHeight = window.innerHeight - 150;
        const result = Math.max(this.minViewportHeight, Math.min(totalHeight, maxAvailableHeight));
        // console.log('[FacilitySelect] viewportHeight:', {
        //     itemCount: items.length,
        //     contentHeight,
        //     totalHeight,
        //     maxAvailableHeight,
        //     result,
        // });

        return result;
    });

    readonly averageItemHeight = computed(() => {
        const items = this.flatList();
        if (!items.length) return this.itemSize;
        const totalHeight = items.reduce((sum, item) => sum + this.estimateItemHeight(item), 0);
        return totalHeight / items.length;
    });

    private readonly updateStrategyEffect = effect(() => {
        this.flatList();
        this.virtualScrollStrategy.updateItemHeights();
    });

    private estimateItemHeight(item: FlatListItem): number {
        if (item.type === 'header' || item.type === 'inactive-header') {
            const textLength = item.headerText?.length || 0;
            return textLength > this.headerCharThreshold ?
                this.headerHeightMultiLine :
                this.headerHeightSingleLine;
        }

        // facility type
        const hasAdminActions = this.adminView && !!item.option?.organization;
        const nameLength = item.option?.name?.length || 0;
        const threshold = hasAdminActions ? this.facilityCharThreshold : this.facilityCharThresholdNoActions;
        const isMultiLine = nameLength > threshold;

        if (hasAdminActions) {
            return isMultiLine ?
                this.facilityHeightMultiLineWithActions :
                this.facilityHeightSingleLineWithActions;
        }

        return isMultiLine ?
            this.facilityHeightMultiLine :
            this.facilityHeightSingleLine;
    }

    @HostListener('document:keydown', ['$event'])
    keyboardNavigation(keyDownEvent: KeyboardEvent): void {
        if (!this.open()) return;

        const currentIndex = this.keyboardSelectedIndex();

        switch (keyDownEvent.key) {
            case 'ArrowUp':
                keyDownEvent.preventDefault();
                this.selectOptionByOffset(currentIndex, -1, this.options().length - 1);
                return;
            case 'ArrowDown':
                keyDownEvent.preventDefault();
                this.selectOptionByOffset(currentIndex, 1, 0);
                return;
            case 'Enter':
                keyDownEvent.preventDefault();
                const selectedOption = this.options()[currentIndex];
                if (selectedOption) this.selectFacility(selectedOption);
                return;
            case 'Escape':
                this.fc.openSelect$.next(false);
        }
    }

    private selectOptionByOffset(currentIndex: number, offset: number, defaultIndex: number): void {
        const targetIndex = currentIndex === -1 ? defaultIndex : currentIndex + offset;
        const opts = this.options();

        if (targetIndex < 0 || targetIndex >= opts.length) return;

        if (currentIndex !== -1 && opts[currentIndex]) {
            opts[currentIndex].keyboardSelected = false;
        }
        opts[targetIndex].keyboardSelected = true;

        this.keyboardSelectedIndex.set(targetIndex);
        this.scrollSelectionIntoView();
    }

    constructor(
        private userAuth: UserAuthService,
        private fc: FacilityChooserService,
        private overlayService: OverlayService,
        private virtualScrollStrategy: EstimatedHeightVirtualScrollStrategy,
    ) {
        super();

        this.virtualScrollStrategy.setContentPadding(this.contentWrapperPadding);
        this.virtualScrollStrategy.setHeightFunctions(
            index => {
                const item = this.flatList()[index];
                return item ? this.estimateItemHeight(item) : this.itemSize;
            },
            () => this.flatList().length,
        );

        this.fc.getSilently().pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.selected = this.fc.selectedDetails;
        });

        this.userAuth.user.pipe(takeUntil(this.destroy$)).subscribe(u => {
            if (!u) this.close();

            this._user = u;
            this.adminView = u?.permissions.includes('PERMISSION_WRITE_ORGANIZATION');
            this.organizations = u ? u.organizations : [];
            this.originalOptions = u ? this.fc.options.filter(x => !x.from_customer_only).map(option => ({...option, keyboardSelected: false})) : [];
            this.options.set(this.originalOptions.map(x => ({...x})));
        });
    }

    ngOnInit() {
        moveDOMNodeToDocumentEnd('.facility-select-options-wrapper');
        this.fc.openSelect$.subscribe((open?: boolean) => {
            this.open.set(!!open);
            this.handleInput(!!open);
        });
    }

    selectFacility(option: OrganizationFacilityDetails) {
        this.fc.setSelectedFacility(option.value);
        this.close();
        this.overlayService.emit(null);
    }

    toggle(event: Event): void {
        event.stopPropagation();
        const newOpen = !this.open();
        this.open.set(newOpen);
        this.clearSelection();
        this.handleInput(newOpen);
    }

    close(): void {
        this.open.set(false);
        this.clearSelection();
        this.handleInput(false);
    }

    scrollSelectionIntoView(): void {
        const selectedIndex = this.keyboardSelectedIndex();
        if (selectedIndex === -1 || !this.virtualScroll) return;

        const selectedOption = this.options()[selectedIndex];
        const flatIndex = this.flatList().findIndex(item =>
            item.type === 'facility' && item.option === selectedOption
        );

        if (flatIndex !== -1) {
            const viewportHeight = this.virtualScroll.getViewportSize();
            const visibleItemCount = Math.floor(viewportHeight / this.averageItemHeight());
            const centeredIndex = Math.max(0, flatIndex - Math.floor(visibleItemCount / 2));
            this.virtualScroll.scrollToIndex(centeredIndex, 'smooth');
        }
    }

    clearSelection(): void {
        const currentIndex = this.keyboardSelectedIndex();
        const opts = this.options();
        if (currentIndex !== -1 && opts[currentIndex]) {
            opts[currentIndex].keyboardSelected = false;
        }
        this.keyboardSelectedIndex.set(-1);
    }

    onFacilitySearchChange(searchString: string): void {
        this.facilitySearchString.set(searchString);
        this.clearSelection();

        const filtered = this.originalOptions
            .map(x => ({...x}))
            .filter(option => !searchString || facilityOptionSearchFn(searchString, option));
        this.options.set(filtered);
        this.selectFirstOption();
    }

    private selectFirstOption(): void {
        this.options.update(opts => {
            if (opts[0]) {
                opts[0].keyboardSelected = true;
                this.keyboardSelectedIndex.set(0);
            }
            return opts; // Return same array reference
        });
    }

    handleInput(open: boolean): void {
        if (!this._user) return;

        this.facilitySearchString.set(null);
        if (open) {
            this.handleOpen();
        } else {
            this.handleClose();
        }
    }

    private handleOpen(): void {
        setTimeout(() => {
            this.facilitySearchInputRef?.nativeElement?.focus();
            this.scrollToSelected();
        });

        const currentIndex = this.keyboardSelectedIndex();

        this.options.update(opts => {
            if (currentIndex !== -1 && opts[currentIndex]) {
                opts[currentIndex].keyboardSelected = false;
            }

            const selectedIndex = opts.findIndex(option => this.selected?.name === option.name);
            const targetIndex = selectedIndex !== -1 ? selectedIndex : 0;

            if (opts[targetIndex]) {
                opts[targetIndex].keyboardSelected = true;
                this.keyboardSelectedIndex.set(targetIndex);
            }

            return opts;
        });
    }

    private handleClose(): void {
        if (this.facilitySearchInputRef?.nativeElement) {
            this.facilitySearchInputRef.nativeElement.value = '';
        }
        this.options.set(this.originalOptions);
    }

    private buildFlatListFromOptions(options: OrganizationFacilityDetailsViewModel[]): FlatListItem[] {
        const result: FlatListItem[] = [];
        let lastOrganization: OrganizationSerializer | null = null;
        let addedInactiveHeader = false;

        for (const option of options) {
            const isInactive = option.facility?.is_inactive;

            if (isInactive && !addedInactiveHeader) {
                result.push({type: 'inactive-header', headerText: 'Inactive Facilities'});
                addedInactiveHeader = true;
                lastOrganization = null;
            }

            if (!isInactive && option.organization && option.organization !== lastOrganization) {
                result.push({type: 'header', headerText: option.organization.name});
                lastOrganization = option.organization;
            }

            result.push({
                type: 'facility',
                option,
            });
        }

        return result;
    }

    scrollToSelected(): void {
        if (!this.virtualScroll) return;

        const flat = this.flatList();
        const selectedIndex = flat.findIndex(item =>
            item.type === 'facility' && item.option?.name === this.selected?.name
        );

        if (selectedIndex !== -1) {
            const centeredIndex = Math.max(0, selectedIndex - 3);
            this.virtualScroll.scrollToIndex(centeredIndex, 'auto');
        }
    }

    @MethodCache()
    getAdditionalInfo(facility: Facility) {
        if (!facility) return null;
        return Facility.getAdditionalInfo(facility, this.adminView);
    }
}

export function moveDOMNodeToDocumentEnd(selectors: string) {
    const node = document.querySelector(selectors);
    const bodyNode = document.querySelector('body');
    if (node && bodyNode) bodyNode.appendChild(node);
}
